import type {
  ContractResponsibilities,
  ContractResponsibilityArea,
  Department,
} from "@closer/shared";
import {
  CONTRACT_RESPONSIBILITY_AREA_NAMES,
  CONTRACT_RESPONSIBILITY_AREAS,
  CONTRACT_RESPONSIBILITY_KIND_NAMES,
} from "@closer/shared";
import { Button, FormField, Modal, Select, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { UyeListesiDurumu } from "../../components/UyeListesiDurumu";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { sozlesmeAdaylariSorgusu } from "../../lib/sharedQueries";
import { tenantKey } from "../../lib/tenantQuery";

/**
 * Diyaloğun ürettiği atama: Sözleşme Sorumlusu (`owner`), alan sorumlusu (`contributor` +
 * `area`) ya da genel katkı sağlayan (`contributor`, alan yok). Destekleyen ekip opsiyonel.
 */
export type ResponsibleAssignment = {
  kind: "owner" | "contributor";
  area?: ContractResponsibilityArea;
  membershipId: string;
  supportingDepartmentId?: string;
};

/** Tür seçicisinin değeri: `owner`, alan anahtarı ya da genel `contributor`. */
type Tur = "owner" | "contributor" | ContractResponsibilityArea;

const TUR_SECENEKLERI: { value: Tur; label: string }[] = [
  { value: "owner", label: CONTRACT_RESPONSIBILITY_KIND_NAMES.owner },
  ...CONTRACT_RESPONSIBILITY_AREAS.map((a) => ({
    value: a,
    label: CONTRACT_RESPONSIBILITY_AREA_NAMES[a],
  })),
  { value: "contributor", label: CONTRACT_RESPONSIBILITY_KIND_NAMES.contributor },
];

function atama(tur: Tur, membershipId: string, ekip: string): ResponsibleAssignment {
  const destek = ekip ? { supportingDepartmentId: ekip } : {};
  if (tur === "owner") return { kind: "owner", membershipId, ...destek };
  if (tur === "contributor") return { kind: "contributor", membershipId, ...destek };
  return { kind: "contributor", area: tur, membershipId, ...destek };
}

/**
 * İÇ SORUMLU EKLE — tek diyalog (CLOS-1126 · rapor E.1/E.4 · Halit K1/K2/K6).
 *
 * Akış: üye → sorumluluk türü → opsiyonel destekleyen ekip → "Ata". Ön koşul çözümü
 * (org yetkisi, kayıt erişimi, "Erişim ver ve ata") SUNUCUDA; bu diyalog yalnız isteği
 * kurar. Alan başına tek kişi: seçilen türün mevcut sorumlusu varsa değişim BİLGİSİ burada
 * görünür (409 değil, sunucu atomik değişir — K1). Destekleyen ekip yalnız etiket; erişim
 * vermez (K2) ve "İlgili departman" satırıyla aynı kavram değil.
 *
 * ⚠ Gönderilen `membershipId`, `userId` DEĞİL: sorumluluk o organizasyondaki üyeliğe bağlı.
 */
export function ContractResponsibleDialog({
  open,
  onClose,
  onConfirm,
  isPending,
  contractId,
  mevcut,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (girdi: ResponsibleAssignment) => void;
  isPending: boolean;
  /**
   * Aday listesinin bağlamı (Mega E2E BUG-002).
   *
   * ⚠ Liste organizasyon üye dizininden DEĞİL sözleşmeden okunuyor: dizinin kapısı
   * `organization.member`, yani yönetim yetkisi — sözleşmeye sorumlu atayabilen ama
   * organizasyonu yönetmeyen kullanıcı seçiciyi boş görüyordu.
   */
  contractId: string;
  /** Sayfadaki sorumluluk listesi — yalnız "yerine geçer" notu için; yoksa not çizilmez. */
  mevcut?: ContractResponsibilities;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const [secili, setSecili] = useState("");
  const [tur, setTur] = useState<Tur>("owner");
  const [ekip, setEkip] = useState("");

  const uyeler = useQuery({
    enabled: open,
    ...sozlesmeAdaylariSorgusu(tenantId, contractId),
  });
  const departmanlar = useQuery({
    enabled: open,
    queryKey: tenantKey(tenantId, "departmanlar", "sozlesme-secimi"),
    queryFn: async () => (await api<Department[]>("/api/departments", { tenantId })).data,
  });
  const atanabilir = uyeler.data?.candidates ?? [];
  const ekipler = (departmanlar.data ?? []).filter((d) => d.archivedAt === null);

  useEffect(() => {
    if (open) {
      setSecili("");
      setTur("owner");
      setEkip("");
    }
  }, [open]);

  /** Seçilen türün MEVCUT sorumlusu (başka kişiyse) — kullanıcı değişimi bilerek yapsın. */
  const yerineGececek = (() => {
    if (!mevcut || secili === "") return null;
    const satir =
      tur === "owner"
        ? mevcut.owner
        : tur === "contributor"
          ? null
          : (mevcut.contributors.find((k) => k.area === tur) ?? null);
    if (!satir || satir.membershipId === secili) return null;
    return {
      unvan:
        tur === "owner"
          ? CONTRACT_RESPONSIBILITY_KIND_NAMES.owner
          : TUR_SECENEKLERI.find((s) => s.value === tur)!.label,
      ad: satir.displayName ?? "—",
    };
  })();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="İç sorumlu ekle"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={() => onConfirm(atama(tur, secili, ekip))}
            loading={isPending}
            disabled={secili === ""}
          >
            Ata
          </Button>
        </>
      }
    >
      {uyeler.isPending ? (
        <div role="status" aria-label="Üyeler yükleniyor">
          <Skeleton lines={2} />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <UyeListesiDurumu hata={uyeler.error} bosMu={atanabilir.length === 0} />
          <FormField label="Üye" required>
            <Select aria-label="Üye" value={secili} onChange={(e) => setSecili(e.target.value)}>
              <option value="">Seçin</option>
              {atanabilir.map((u) => (
                <option key={u.membershipId} value={u.membershipId}>
                  {u.name}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Sorumluluk türü" required>
            <Select
              aria-label="Sorumluluk türü"
              value={tur}
              onChange={(e) => setTur(e.target.value as Tur)}
            >
              {TUR_SECENEKLERI.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField
            label="Destekleyen ekip"
            hint="Yalnız bilgi amaçlı; ekibin üyelerine sözleşme erişimi vermez."
          >
            <Select
              aria-label="Destekleyen ekip"
              value={ekip}
              onChange={(e) => setEkip(e.target.value)}
            >
              <option value="">Yok</option>
              {ekipler.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
          </FormField>
          {yerineGececek ? (
            <p className="text-sm text-fg-muted" data-testid="yerine-gecer">
              Mevcut {yerineGececek.unvan}: {yerineGececek.ad} — bu atama onun yerine geçer.
            </p>
          ) : null}
          <p className="text-xs text-fg-soft">
            Sorumluluk erişim ya da yetki vermez; kişinin sözleşme erişimi yoksa bir sonraki adımda
            açıkça sorulur.
          </p>
        </div>
      )}
    </Modal>
  );
}
